/**
 * Websocket sync provider — connects the timeline Y.Doc to the room the Python
 * sync service hosts for this project (`backend/sync/`).
 *
 * Two rules shape this file.
 *
 * **Connect empty and let sync fill the document.** `initEmptyDocForSync()`
 * hands over a blank Y.Doc; the server is the only thing that seeds a room from
 * `Project.timeline`. If the client seeded too, the two builders would produce
 * unrelated CRDT histories, and merging them resolves whole id-keyed subtrees
 * last-write-wins instead of converging.
 *
 * **A disconnected editor is not an editable editor.** With sync on, the
 * timeline lives in the room and there is no JSON autosave to fall back on, so
 * edits made while disconnected reach nothing. Callers must gate the editor on
 * `synced` — see ConnectionState — rather than showing an empty timeline that
 * looks like an empty project.
 *
 * Remote updates need no plumbing here: they arrive with the provider as their
 * origin, which is neither LOCAL_ORIGIN nor NO_UNDO_ORIGIN, so the store's
 * existing `onDocUpdate` observer materializes them — and because the origin is
 * not in the UndoManager's `trackedOrigins`, a peer's edits never enter your
 * undo stack.
 */
import type { Awareness } from 'y-protocols/awareness';
import { WebsocketProvider } from 'y-websocket';
import type * as Y from 'yjs';
import { initEmptyDocForSync } from '../state/timeline';

export type ConnectionStatus =
  | 'connecting'
  /** Connected and the document has arrived — safe to render and edit. */
  | 'synced'
  /** Was synced, lost the socket; y-websocket is retrying. Edits still merge. */
  | 'reconnecting'
  /** Never got a document. Refused (bad token / not yours) or service down. */
  | 'failed';

export interface SyncHandle {
  doc: Y.Doc;
  provider: WebsocketProvider;
  /**
   * Presence channel. y-websocket constructs this for us and the Python sync
   * service relays it between clients, so it works with no backend support —
   * see state/presence.ts.
   */
  awareness: Awareness;
  destroy(): void;
}

export interface ConnectOptions {
  projectId: number | string;
  /** Collab-link token; omitted for the owner, who authenticates by session. */
  token?: string;
  onStatus(status: ConnectionStatus): void;
}

/** Raw y-websocket events, as the reducer sees them. */
export type SyncEvent =
  | { kind: 'sync'; isSynced: boolean }
  | { kind: 'status'; status: string };

export interface SyncMachineState {
  everSynced: boolean;
}

export const INITIAL_SYNC_STATE: SyncMachineState = { everSynced: false };

/**
 * The connection state machine, as a pure function so it can be table-tested —
 * it gates whether the editor renders at all. `emit: null` means stay quiet
 * (socket-level 'connected' waits for 'sync'; unknown statuses are ignored).
 */
export function reduceSyncEvent(
  state: SyncMachineState,
  event: SyncEvent,
): { state: SyncMachineState; emit: ConnectionStatus | null } {
  if (event.kind === 'sync') {
    if (!event.isSynced) return { state, emit: null };
    return { state: { everSynced: true }, emit: 'synced' };
  }
  switch (event.status) {
    case 'connecting':
      return { state, emit: state.everSynced ? 'reconnecting' : 'connecting' };
    case 'disconnected':
      // Never synced means we were refused or nothing is listening. Retrying
      // will not fix either, but y-websocket keeps trying regardless; surface
      // it as failed so the UI can say so instead of spinning forever.
      return { state, emit: state.everSynced ? 'reconnecting' : 'failed' };
    default:
      return { state, emit: null }; // 'connected' waits for 'sync'
  }
}

/** Room path served by nginx `location /sync/` (vite proxies it in dev). */
export function syncUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/sync`;
}

export function connectTimelineSync(options: ConnectOptions): SyncHandle {
  const { projectId, token, onStatus } = options;
  const doc = initEmptyDocForSync();

  const provider = new WebsocketProvider(syncUrl(), `project-${projectId}`, doc, {
    // The handshake authorizes on the session cookie or this token; the server
    // refuses before the upgrade, so a rejection surfaces as a failed connect.
    params: token ? { token } : {},
  });

  let machine = INITIAL_SYNC_STATE;
  const dispatch = (event: SyncEvent) => {
    const next = reduceSyncEvent(machine, event);
    machine = next.state;
    if (next.emit) onStatus(next.emit);
  };

  provider.on('sync', (isSynced: boolean) => dispatch({ kind: 'sync', isSynced }));
  provider.on('status', (event: { status: string }) => dispatch({ kind: 'status', status: event.status }));

  return {
    doc,
    provider,
    awareness: provider.awareness,
    destroy() {
      provider.disconnect();
      provider.destroy();
    },
  };
}
